import { Patterns } from "@omni/schemas/input/patterns";
import { BaseStore } from "@omni/types/enums/baseStore.enum";
import { BonusCardType } from "@omni/types/enums/bonusCardType.enum";
import { AxiosError } from "axios";
import { addMethod, StringSchema, string as yupString } from "yup";

import { create } from "@/api/api";
import { Translate } from "@/redux/modules/value/translations/translations.provider";
import { Config } from "@/utils/config.utils";

import { string } from "./general.RHF";

const BonusCardAPI = create("", true);

function createBonusCardTest({
    name,
    type,
    required,
    formatOnly
}: {
    name: string;
    type: keyof (typeof Patterns)[BaseStore]["bonusCard"];
    required: boolean;
    formatOnly: boolean;
}) {
    return function (this: StringSchema) {
        return this.test(name, async function (value, { path }) {
            if (!required && !value) return true;

            const pattern = Patterns[Config.general.basestore].bonusCard[type];

            if (!pattern || !value || !pattern.test(value)) {
                return this.createError({
                    path,
                    message: () => () => (t: Translate) =>
                        t("general.form.input.validation.bonusCard.invalid")
                });
            }

            if (formatOnly) return true;

            try {
                await BonusCardAPI.request({
                    method: "get",
                    url: `${Config.general["api-base-url"]}/customerservice/${Config.general.basestore}/bonuscards/${value}`,
                    headers: {
                        cache: "force-cache",
                        timeout: 5000,
                        mode: "cors"
                    }
                });

                return true;
            } catch (error) {
                const errorCode = (error as AxiosError).response?.data;
                const errorMessage = errorCode
                    ? `general.form.input.validation.bonusCard.${errorCode}`
                    : "general.form.input.validation.bonusCard.invalid";

                return this.createError({
                    path,
                    message: () => () => (t: Translate) => t(errorMessage)
                });
            }
        });
    };
}

addMethod<StringSchema>(yupString, "physicalBonusCardRHF", function (required) {
    return createBonusCardTest({
        name: "physicalBonusCardRHF",
        type: BonusCardType.Physical,
        required: required,
        formatOnly: false
    }).call(this);
});

addMethod<StringSchema>(yupString, "eBonusCardRHF", function (required) {
    return createBonusCardTest({
        name: "eBonusCardRHF",
        type: BonusCardType.Electronic,
        required: required,
        formatOnly: false
    }).call(this);
});

addMethod<StringSchema>(yupString, "bonusCardRHF", function (required) {
    return createBonusCardTest({
        name: "bonusCardRHF",
        type: "anyBonusCard",
        required: required,
        formatOnly: false
    }).call(this);
});

addMethod<StringSchema>(
    yupString,
    "physicalBonusCardFormatRHF",
    function (required) {
        return createBonusCardTest({
            name: "physicalBonusCardFormatRHF",
            type: BonusCardType.Physical,
            required: required,
            formatOnly: true
        }).call(this);
    }
);

addMethod<StringSchema>(yupString, "eBonusCardFormatRHF", function (required) {
    return createBonusCardTest({
        name: "eBonusCardFormatRHF",
        type: BonusCardType.Electronic,
        required: required,
        formatOnly: true
    }).call(this);
});

addMethod<StringSchema>(yupString, "bonusCardFormatRHF", function (required) {
    return createBonusCardTest({
        name: "bonusCardFormatRHF",
        type: "anyBonusCard",
        required: required,
        formatOnly: true
    }).call(this);
});

export const physicalBonusCard = string()
    .nullable()
    .required()
    .physicalBonusCardRHF(true);

export const eBonusCard = string().nullable().required().eBonusCardRHF(true);

export const bonusCard = string().nullable().required().bonusCardRHF(true);

export const optionalPhysicalBonusCard = string()
    .nullable()
    .physicalBonusCardRHF(false);

export const optionalEBonusCard = string().nullable().eBonusCardRHF(false);

export const optionalBonusCard = string().nullable().bonusCardRHF(false);

export const physicalBonusCardFormat = string()
    .nullable()
    .required()
    .physicalBonusCardFormatRHF(true);

export const eBonusCardFormat = string()
    .nullable()
    .required()
    .eBonusCardFormatRHF(true);

export const bonusCardFormat = string()
    .nullable()
    .required()
    .bonusCardFormatRHF(true);

export const optionalPhysicalBonusCardFormat = string()
    .nullable()
    .physicalBonusCardFormatRHF(false);

export const optionalEBonusCardFormat = string()
    .nullable()
    .eBonusCardFormatRHF(false);

export const optionalBonusCardFormat = string()
    .nullable()
    .bonusCardFormatRHF(false);
